/* Data hooks: load on mount/deps change, run actions with toasts, paged lists with URL-free state. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, errorMessage } from '../api/client';
import { useToast } from '../components/overlay';

export interface ApiState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (d: T | undefined) => void;
}

/** Load data with `fn`; re-runs when `deps` change. Pass `skip` to wait for a prerequisite. */
export function useApi<T>(fn: () => Promise<T>, deps: unknown[] = [], opts: { skip?: boolean } = {}): ApiState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(!opts.skip);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (opts.skip) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    fnRef.current().then(
      (d) => { if (alive) { setData(d); setLoading(false); } },
      (e) => { if (alive) { setError(errorMessage(e)); setLoading(false); } },
    );
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, opts.skip]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload, setData };
}

/** Run a write; shows a success toast or the server's error (409s explained). Returns the result or undefined. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <R,>(fn: () => Promise<R>, opts: { success?: string; rethrow?: boolean } = {}): Promise<R | undefined> => {
    setBusy(true);
    try {
      const r = await fn();
      if (opts.success) toast(opts.success, 'success');
      return r;
    } catch (e) {
      const tone = e instanceof ApiError && e.status === 409 ? 'warning' : 'error';
      toast(errorMessage(e), tone);
      if (opts.rethrow) throw e;
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [toast]);
  return { run, busy };
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  sort: string | undefined;
  filters: Record<string, string>;
  loading: boolean;
  error: string | null;
  setPage: (p: number) => void;
  setSort: (s: string | undefined) => void;
  setFilter: (k: string, v: string) => void;
  reload: () => void;
}

type PageResult<T> = { items: T[]; total: number; page: number; size: number };

/** Server-paged list (page, size, sort + filters). Empty filter values are not sent. */
export function usePaged<T>(fn: (q: Record<string, string | number | undefined>) => Promise<PageResult<T>>,
  opts: { size?: number; filters?: Record<string, string>; sort?: string } = {}): Paged<T> {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string | undefined>(opts.sort);
  const [filters, setFilters] = useState<Record<string, string>>(opts.filters ?? {});
  const size = opts.size ?? 15;
  const q: Record<string, string | number | undefined> = { page, size, sort };
  for (const [k, v] of Object.entries(filters)) if (v !== '') q[k] = v;
  const st = useApi(() => fn(q), [page, sort, size, JSON.stringify(filters)]);
  return {
    items: st.data?.items ?? [],
    total: st.data?.total ?? 0,
    page, size, sort, filters,
    loading: st.loading,
    error: st.error,
    setPage,
    setSort,
    setFilter: (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); },
    reload: st.reload,
  };
}

/** Download a Blob returned by a file endpoint. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Open a Blob (a PDF) in a new tab. */
export function openBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
